import { skipToken } from '@reduxjs/toolkit/query';
import { useDebouncedFunction } from '@strata/helper-functions';
import { ChatWithConversationPane } from '@strata/react-chat';
import { SuccessToast } from '@strata/react-toast-message';
import { type ComponentProps, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { NewNotebookDialog } from './dialogs/NewNotebookDialog';

import { Permissions } from 'services/constants';
import { useAppSelector } from 'store';
import { useGetLighthouseUserNameMapQuery } from 'store/api/authApi';
import {
    useDeleteNotebookMutation,
    useDeleteNoteMutation,
    useGetNotebooksForIntakeCaseQuery,
    useGetNotesForNotebookQuery,
    usePostCreateNoteMutation,
} from 'store/api/intakeCaseApi';

export const NotesTab = () => {
    const { intakeCaseId } = useParams() as { intakeCaseId: string };

    const permissions = useAppSelector((state) => state.auth.permissions);
    const chatDisabled = !permissions.includes(Permissions.WRITE_INTAKE_CASE);

    const [searchText, setSearchText] = useState<string>();
    const debouncedSearch = useDebouncedFunction();

    const { data: userNameMap } = useGetLighthouseUserNameMapQuery();

    const { data: notebooks, isFetching: notebooksFetching } = useGetNotebooksForIntakeCaseQuery({
        intakeCaseId,
        searchText,
    });
    const mappedNotebooks = notebooks?.map((notebook) => {
        const [createdByFirstName, createdByLastName] =
            userNameMap?.[notebook.createdByUpn]?.split(' ') ?? [];
        return { ...notebook, createdByFirstName, createdByLastName };
    });

    const [selectedConversationId, setSelectedConversationId] = useState<string>();
    const selectedNotebook = mappedNotebooks?.find(
        ({ conversationId }) => conversationId === selectedConversationId,
    );
    useEffect(() => {
        if (mappedNotebooks && mappedNotebooks.length > 0 && !selectedConversationId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedConversationId(mappedNotebooks[0].conversationId);
        }
    }, [mappedNotebooks, selectedConversationId]);

    const { data: notes, isFetching: notesFetching } = useGetNotesForNotebookQuery(
        selectedConversationId ? { notebookId: selectedConversationId } : skipToken,
    );

    const selectedConversation = selectedNotebook && {
        ...selectedNotebook,
        messages: notes?.map((note) => {
            const [senderFirstName, senderLastName] = userNameMap?.[note.upn]?.split(' ') ?? [];
            return { ...note, senderFirstName, senderLastName };
        }),
    };

    const [showNewNoteDialog, setShowNewNoteDialog] = useState(false);

    const [deleteNotebook, { isLoading: deleteNotebookLoading }] = useDeleteNotebookMutation();
    const [postCreateNote, { isLoading: postCreateNoteLoading }] = usePostCreateNoteMutation();
    const [deleteNote, { isLoading: deleteNoteLoading }] = useDeleteNoteMutation();

    const onConversationDelete: ComponentProps<
        typeof ChatWithConversationPane
    >['onConversationDelete'] = async (notebookId) => {
        await deleteNotebook({ intakeCaseId, notebookId }).unwrap();
        SuccessToast({ message: 'Notebook deleted successfully.' });
        if (notebookId === selectedConversationId) {
            setSelectedConversationId(undefined);
        }
    };

    const onMessageDelete: ComponentProps<
        typeof ChatWithConversationPane
    >['onMessageDelete'] = async ({ conversationId, messageId }) => {
        await deleteNote({
            notebookId: conversationId,
            noteId: messageId,
        }).unwrap();
        SuccessToast({ message: 'Note deleted successfully.' });
    };

    return (
        <>
            <NewNotebookDialog
                isOpen={showNewNoteDialog}
                intakeCaseId={intakeCaseId}
                onCancel={() => setShowNewNoteDialog(false)}
                onCreateSuccess={(newNotebookId) => {
                    setSelectedConversationId(newNotebookId);
                    setShowNewNoteDialog(false);
                }}
            />
            <ChatWithConversationPane
                conversations={mappedNotebooks}
                chatDisabled={chatDisabled}
                conversationType={ChatWithConversationPane.ConversationType.NOTES}
                highlightText={searchText}
                chatLoading={
                    notebooksFetching ||
                    notesFetching ||
                    deleteNotebookLoading ||
                    postCreateNoteLoading ||
                    deleteNoteLoading
                }
                onSearch={(value) =>
                    debouncedSearch(() => {
                        setSearchText(value || undefined);
                        setSelectedConversationId(undefined);
                    })
                }
                onConversationSelect={setSelectedConversationId}
                selectedConversation={selectedConversation}
                onNewConversationClick={() => setShowNewNoteDialog(true)}
                onMessageSend={async ({ conversationId, details }) => {
                    await postCreateNote({
                        notebookId: conversationId,
                        content: details,
                    }).unwrap();
                    SuccessToast({ message: 'Note saved successfully.' });
                }}
                onConversationDelete={!chatDisabled ? onConversationDelete : undefined}
                onMessageDelete={!chatDisabled ? onMessageDelete : undefined}
            />
        </>
    );
};
